/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import axios from 'axios';
import { LifeApiService } from '../managers/sso';
import { ProfileResponse } from '../models/LifeProfileResponse';
import { AccountEndpoints } from '../config/EnvironmentURLs';
import { useAccountUrl } from './useAccountUrl';
import {
  accountKeys,
  useAccessToken,
  useApiKey,
  useApiQuery,
  useUserId,
} from './account';

import { useMemo } from 'react';

export function useLifeApiService(): LifeApiService {
  const lifeApiBaseUrl = useAccountUrl(AccountEndpoints.LIFE_API_BASE_URL);
  return useMemo(() => new LifeApiService(lifeApiBaseUrl), [lifeApiBaseUrl]);
}

export const useLifeProfile = (caller: string) => {
  const api = useLifeApiService();
  const userIdQuery = useUserId(caller);
  const apikeyQuery = useApiKey();
  const accessTokenQuery = useAccessToken('useLifeProfile');

  const enabledCondition =
    (userIdQuery.data?.length || 0) > 0 &&
    apikeyQuery.isTokenValid &&
    accessTokenQuery.isTokenValid;

  return useApiQuery<ProfileResponse, Error>({
    queryKey: accountKeys.lifeProfile,
    staleTime: 20 * 60 * 1000,

    queryFn: async () => {
      const [apiKeyData, token] = await Promise.all([
        apikeyQuery.getToken(),
        accessTokenQuery.getToken(),
      ]);

      if (!apiKeyData?.apiKey) throw new Error('Missing API key');
      if (!apiKeyData?.id) throw new Error('Missing Client ID');
      if (!userIdQuery.data) throw new Error('Missing user ID');
      if (!token) throw new Error('Missing access token');

      try {
        return await api.getProfile(
          apiKeyData.apiKey,
          apiKeyData.id,
          userIdQuery.data,
          token,
        );
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          return {} as ProfileResponse;
        }
        throw error;
      }
    },

    enabled: enabledCondition,
  });
};
